import { useEffect, useState } from 'react';
import { t } from '../i18n/index.ts';
import { request } from '../lib/api.ts';
import { Icon } from './Icon.tsx';
import { IconButton } from './ui.tsx';

interface Announcement {
  id: string;
  title: string;
  body: string | null;
  level: 'info' | 'warn';
  ends_at: string;
}

const KEY = 'erp.announcements.closed';
const closedIds = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '[]') as string[];
  } catch {
    return [];
  }
};

/**
 * Messages from server administration ("Maintenance tonight 23:00–23:15"), at the top of the
 * app until they end or the reader closes them on this device. Checked every five minutes.
 */
export function AnnouncementBar() {
  const [items, setItems] = useState<Announcement[]>([]);
  const [closed, setClosed] = useState<string[]>(closedIds);
  useEffect(() => {
    let stop = false;
    const load = () =>
      request<Announcement[]>('GET', '/api/announcements').then(
        (a) => !stop && setItems(a),
        () => {
          /* offline: keep what is shown */
        },
      );
    void load();
    const timer = window.setInterval(load, 5 * 60_000);
    return () => {
      stop = true;
      window.clearInterval(timer);
    };
  }, []);
  const shown = items.filter((a) => !closed.includes(a.id));
  if (!shown.length) return null;
  const close = (id: string) => {
    // Only ids still shown are kept, so the list never grows.
    const next = [...closed.filter((x) => items.some((a) => a.id === x)), id];
    setClosed(next);
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      /* private mode: closed until reload */
    }
  };
  return (
    <>
      {shown.map((a) => (
        <div key={a.id} className={`announce announce-${a.level}`} role={a.level === 'warn' ? 'alert' : 'status'}>
          <Icon name={a.level === 'warn' ? 'alert' : 'bell'} size={18} />
          <div className="announce-main">
            <strong>{a.title}</strong>
            {a.body ? <p>{a.body}</p> : null}
          </div>
          <IconButton icon="x" label={t('common.close')} onClick={() => close(a.id)} />
        </div>
      ))}
    </>
  );
}
